// LayoutOnboardingSeen — レイアウト切替オンボーディング (coachmark) を「一度だけ」
// 出すための seen フラグ永続化 (infrastructure 層).
//
// 背景 (docs/_wip/modern-layout.md §9.4.1 ②、consult 2026-06-08):
//   - グローバル既定 Modern に着地した新規/旧ユーザへ、切替トグルから
//     **非ブロッキング**な coachmark/トースト「クラシック表示にも切り替えられます」を
//     **初回のみ** 表示する。モーダルで止めない (1秒匿名・即描ける gateway 体験を
//     殺さない)。
//   - 「初回のみ」の判定に localStorage の seen フラグを使う。
//
// 設計 (`layoutPreference.ts` 等と同流儀):
//   - boolean を文字列 `'1'` で保存 (= 在れば seen 済)。不在 / storage 不可は
//     「未 seen」(= まだ出す) 扱い。例外を throw しない。
//   - StorageLike 抽象 + sentinel probe。test で storage mock 注入可能。
//
// Tests: layoutOnboardingSeen.test.ts (storage-injection style)。

/** localStorage キー: レイアウト切替オンボーディングを表示済みか. */
export const LAYOUT_ONBOARDING_SEEN_KEY = 'oekaki:layout-onboarding-seen';

/** Minimal Storage shape this module uses (layoutPreference.ts と同流儀). */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface LayoutOnboardingSeenStore {
  /** 既に coachmark を表示したか. 不在 / storage 不可 → false (= まだ出す). */
  hasSeen(): boolean;
  /** coachmark を表示したことを記録. storage 不可なら no-op. */
  markSeen(): void;
  /** key を削除. テスト / リセット用. storage 不可なら no-op. */
  clear(): void;
}

const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:layout-onboarding__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

export interface CreateLayoutOnboardingSeenStoreOptions {
  /**
   * Override the underlying storage. Default: probe `globalThis.localStorage`,
   * fall through to no-op if unavailable. Pass `null` to **force** the no-op
   * path (= hasSeen always false, markSeen silently drops).
   */
  storage?: StorageLike | null;
}

/**
 * `LayoutOnboardingSeenStore` のインスタンスを構築. storage 不可でも例外を出さず、
 * 「未 seen」扱い (= 出すが永続化されないだけ) で動作を継続する.
 */
export const createLayoutOnboardingSeenStore = (
  opts: CreateLayoutOnboardingSeenStoreOptions = {},
): LayoutOnboardingSeenStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());

  return {
    hasSeen() {
      if (storage === null) return false;
      try {
        return storage.getItem(LAYOUT_ONBOARDING_SEEN_KEY) !== null;
      } catch {
        return false;
      }
    },
    markSeen() {
      if (storage === null) return;
      try {
        storage.setItem(LAYOUT_ONBOARDING_SEEN_KEY, '1');
      } catch {
        // QuotaExceeded / SecurityError 等 — 出し直しになるだけで害はない.
      }
    },
    clear() {
      if (storage === null) return;
      try {
        storage.removeItem(LAYOUT_ONBOARDING_SEEN_KEY);
      } catch {
        // ignore
      }
    },
  };
};
